import { Inject, Injectable, InjectionToken } from '@angular/core';
import { FieldMetadata, LinkConfiguration } from '@ddv/models';
import { Observable, ReplaySubject } from 'rxjs';

import { ADD_NEW_CROSSTALK_SCHEMA, Schema } from './comments.service';

export interface CellValue {
    formatted: string;
    original: unknown;
}

export const xtlkLocationToken: InjectionToken<string> = new InjectionToken('xtlkLocationToken');
export const pscLocationToken: InjectionToken<string> = new InjectionToken('pscLocationToken');

/*
    This name is bad.  This service launches all modals, not just Crosstalk
 */
@Injectable()
export class CrosstalkModalService {
    public readonly crosstalkSchema$: Observable<Schema>;

    private activeModal: Window | null = null;
    private readonly crosstalkSchemaSubject = new ReplaySubject<Schema>(1);

    constructor(
        @Inject(xtlkLocationToken) private readonly location: string,
        // does not belong here.  there should be a generic modal service
        @Inject(pscLocationToken) private readonly pscLocation: string,
    ) {
        this.crosstalkSchema$ = this.crosstalkSchemaSubject.asObservable();
        this.crosstalkSchemaSubject.next({} as Schema);
    }

    configureCellForLinking(
        linkConfiguration: LinkConfiguration,
        cellValue: CellValue,
        skipBuildUrl = false,
        fieldMetadata?: FieldMetadata,
    ): string {
        const url = skipBuildUrl ? linkConfiguration.uri : this.buildURLFromURI(linkConfiguration.uri);
        const linkText = deriveLinkText(cellValue, linkConfiguration, fieldMetadata);
        const linkStyle = deriveLinkStyle(cellValue);

        if (this.linkAsAModal(linkConfiguration)) {
            return this.makeModalLink(linkText, linkStyle);
        }

        if (this.linkAsDDVWidget(linkConfiguration)) {
            return this.buildDDVWidgetAsLink(linkText, linkStyle, url);
        }

        if (this.openInSameNewWindow(linkConfiguration)) {
            return this.buildSameNewWindowUrlLink(linkText, linkStyle, url, linkConfiguration.launchType);
        }

        return this.makeNewTabLink(linkText, linkStyle, url);
    }

    // This method is used to open any modal from a grid cell, not just crosstalk
    openModal(linkConfiguration: LinkConfiguration, event: MouseEvent, skipBuildUrl = false): void {
        this.closeModal();

        const url = skipBuildUrl ? linkConfiguration.uri : this.buildURLFromURI(linkConfiguration.uri);

        const widthPart = linkConfiguration.size?.width ? `,width=${linkConfiguration.size.width}` : '';
        const heightPart = linkConfiguration.size?.height ? `,height=${linkConfiguration.size.height}` : '';
        const locationPart = `top=${event.screenY},left=${event.screenX}`;
        const optionsString = `${locationPart},toolbar=0,menubar=0${widthPart}${heightPart}`;

        this.activeModal = window.open(url, '_blank', optionsString);

        if (linkConfiguration.label === ADD_NEW_CROSSTALK_SCHEMA) {
            // eslint-disable-next-line @typescript-eslint/unbound-method
            window.removeEventListener('message', this.handleNewCrosstalkSchema);
            this.updateCrosstalkSchemaSubject({} as Schema);
            window.addEventListener('message', this.handleNewCrosstalkSchema.bind(this));
        }
    }

    linkAsAModal(linkConfiguration: LinkConfiguration): boolean {
        return linkConfiguration.launchType === 'modal';
    }

    linkAsDDVWidget(linkConfiguration: LinkConfiguration): boolean {
        return linkConfiguration.launchType === 'masterdetail';
    }

    getModalPosition(modalWidth: number, modalHeight: number): { screenX: number, screenY: number } {
        // in case the user has dual screen setup
        const dualScreenLeft = window.screenLeft || window.screenX;
        const dualScreenTop = window.screenTop || window.screenY;

        const width = window.innerWidth || document.documentElement.clientWidth || screen.width;
        const height = window.innerHeight || document.documentElement.clientHeight || screen.height;

        const zoom = width / window.screen.availWidth;

        // center modal on screen
        const screenX = ((width - modalWidth) / 2 / zoom) + dualScreenLeft;
        const screenY = ((height - modalHeight) / 2 / zoom) + dualScreenTop;

        return { screenX, screenY };
    }

    closeModal(): void {
        if (this.activeModal) {
            this.activeModal.close();
            this.activeModal = null;
        }
    }

    private buildURLFromURI(uri: string): string {
        let rootLocation = this.location;
        switch (uri.split('/')[1]) {
            case 'psc':
                rootLocation = this.pscLocation;
                break;
            default:
                rootLocation = this.location;
        }

        return `${rootLocation}${uri}`;
    }

    private makeNewTabLink(linkText: string, linkStyle: string, url: string): string {
        return `<a${linkStyle} href="${url}" target="_blank">${linkText}</a>`;
    }

    private makeModalLink(linkText: string, linkStyle: string): string {
        return `<span${linkStyle}>${linkText}</span>`;
    }

    private openInSameNewWindow(linkConfiguration: LinkConfiguration): boolean {
        return linkConfiguration.launchType === '_self' || linkConfiguration.launchType === '_blank';
    }

    private buildSameNewWindowUrlLink(linkText: string, linkStyle: string, url: string, target: string | null = '_self'): string {
        return `<a${linkStyle} href="${(url.indexOf('//') === -1 ? '//' : '') + url}" target="${target}">${linkText}</a>`;
    }

    private buildDDVWidgetAsLink(linkText: string, linkStyle: string, url: string): string {
        return `<span${linkStyle} data-ddv-widget=${url}>${linkText}</span>`;
    }

    private handleNewCrosstalkSchema(event: MessageEvent): void {
        if (event.origin !== window.location.origin) {
            return;
        }

        if (event.data.status === 'success' && event.data.type === 'crosstalk-new-schema') {
            this.updateCrosstalkSchemaSubject(event.data.schema);
        }
    }

    private updateCrosstalkSchemaSubject(data: Schema = {} as Schema): void {
        this.crosstalkSchemaSubject.next({ ...data });
    }
}

function deriveLinkStyle(cellValue: CellValue): string {
    const style = 'external-app-link';

    return isEmptyCellValue(cellValue) ?
        ` class="originally-undefined ${style}"` :
        ` class="${style}"`;
}

function deriveLinkText(
    cellValue: CellValue,
    linkConfiguration: LinkConfiguration,
    fieldMetadata?: FieldMetadata,
): string {
    if (isEmptyCellValue(cellValue)) {
        return fieldMetadata?.isCustomField ?
            '<span class="audit-hover-label">Click for Audit</span>' :
            linkConfiguration.label;
    }

    return cellValue.formatted;
}

function isEmptyCellValue(cellValue: CellValue): boolean {
    return cellValue.original == null || cellValue.original === '';
}
